import { VIEW_TARGETS, type ViewRecord } from "@/shared/domain/views";

import { ApplyViewConfirmation } from "./ApplyViewConfirmation";
import { ApprovalMode } from "./approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "./ClientTools";
import {
  ToolErrorKind,
  classifyToolFailure,
  createClientTool,
  toolError,
  toolSuccess,
} from "./ClientTools";
import {
  coerceApplyViewInput,
  describeViewFailure,
  isApplyViewParams,
  viewListPath,
  type ApplyViewParams,
  type ViewLookup,
} from "./viewToolInput";

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    view_id: {
      type: "string",
      description:
        "The View to open, e.g. 'vw_abc123'. Preferred when known: an ID " +
        "names one View outright. Takes precedence over name.",
    },
    name: {
      type: "string",
      description:
        "The View's name, matched exactly (ignoring case) among the Views " +
        "on one table. Use when the user names a View and no vw_ ID is at " +
        "hand; the tool reports the table's Views if none matches.",
    },
    target: {
      type: "string",
      enum: [...VIEW_TARGETS],
      description:
        "The table to look a name up on. Defaults to the table the user is " +
        "looking at (the client context's table_state); required for a " +
        "name lookup when no resource table is on screen. Ignored with " +
        "view_id.",
    },
    set_as_default: {
      type: "boolean",
      description:
        "Also make this View the user's default for its table, so that table " +
        "opens showing it from now on. The user's own preference, the same one " +
        "the View picker's 'Set as default' writes; it replaces any previous " +
        "default for that table. Use for 'always open sessions with this View'.",
    },
  },
  additionalProperties: false,
};

type ViewService = NonNullable<ClientToolDeps["viewService"]>;

/**
 * The View a lookup names, or the error result to return instead.
 *
 * A name is matched exactly, ignoring case and surrounding whitespace, among
 * every View the caller can see on the table. No match reports the table's
 * Views so the model can correct itself in one turn; more than one match is
 * refused with the candidates, since names are not unique and picking one
 * would open a View the user did not ask for.
 */
async function resolveView(
  lookup: ViewLookup,
  viewService: ViewService,
  orgId: string,
): Promise<
  | { ok: true; view: ViewRecord }
  | { ok: false; result: ClientToolExecutionResult }
> {
  if (lookup.by === "id") {
    try {
      return {
        ok: true,
        view: await viewService.getViewById(lookup.viewId, orgId),
      };
    } catch (err) {
      return {
        ok: false,
        result: toolError(
          `Failed to read View ${lookup.viewId}: ${describeViewFailure(err)}`,
          {},
          classifyToolFailure(err),
        ),
      };
    }
  }

  let views: ViewRecord[];
  try {
    views = await viewService.getAllViews(lookup.target, orgId);
  } catch (err) {
    return {
      ok: false,
      result: toolError(
        `Failed to list the Views on the ${lookup.target} table: ` +
          describeViewFailure(err),
        {},
        classifyToolFailure(err),
      ),
    };
  }

  const wanted = lookup.name.toLowerCase();
  const matches = views.filter(
    (view) => view.name.trim().toLowerCase() === wanted,
  );
  if (matches.length === 1) {
    return { ok: true, view: matches[0] };
  }
  if (matches.length === 0) {
    return {
      ok: false,
      result: toolError(
        `No View named "${lookup.name}" on the ${lookup.target} table that ` +
          "the user can see. The table's Views are listed in available_views.",
        {
          available_views: views.map(({ view_id, name }) => ({
            view_id,
            name,
          })),
        },
        ToolErrorKind.NOT_FOUND,
      ),
    };
  }
  return {
    ok: false,
    result: toolError(
      `${matches.length} Views on the ${lookup.target} table are named ` +
        `"${lookup.name}". Ask the user which one they mean and call this ` +
        "tool again with its view_id; the candidates are listed.",
      {
        candidates: matches.map(({ view_id, name, created_by }) => ({
          view_id,
          name,
          created_by,
        })),
      },
      ToolErrorKind.VALIDATION,
    ),
  };
}

async function execute(
  params: ApplyViewParams,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  // Not TRANSPORT: nothing was attempted, and no retry can supply what the
  // browser context is missing.
  if (!deps?.viewService || !deps.orgId) {
    return toolError(
      "Views are unavailable in this session, so this tool cannot run. " +
        "Nothing was attempted.",
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  const tableOnScreen = deps.resourceTableOnScreen?.();
  // The same verdict the confirmation card reached, so the user cannot approve
  // a proposal that is refused here.
  const verdict = coerceApplyViewInput(params, tableOnScreen);
  if (!verdict.ok) {
    return toolError(
      verdict.reason,
      verdict.details ?? {},
      ToolErrorKind.VALIDATION,
    );
  }

  const resolved = await resolveView(
    verdict.payload,
    deps.viewService,
    deps.orgId,
  );
  if (!resolved.ok) {
    return resolved.result;
  }
  const { view } = resolved;

  // The default is written before the View is opened, so a failed write
  // leaves nothing half done: the table is untouched and the model is told.
  if (verdict.payload.setAsDefault) {
    if (deps.setDefaultView === undefined) {
      return toolError(
        "A default View cannot be set in this session, so this tool did " +
          "nothing. Call it again without set_as_default to open the View.",
        {},
        ToolErrorKind.VALIDATION,
      );
    }
    try {
      await deps.setDefaultView(view.target, view.view_id);
    } catch (err) {
      const kind = classifyToolFailure(err);
      return toolError(
        `Failed to make View ${view.view_id} the default for the ${view.target} ` +
          `table: ${describeViewFailure(err)}. The View was not opened.`,
        kind === ToolErrorKind.TRANSPORT
          ? {
              // Writing the same preference twice lands the same way.
              retry_safe: true,
              recovery:
                "Setting a default is idempotent: it is safe to call this " +
                "tool again with the same arguments.",
            }
          : {},
        kind,
      );
    }
  }

  // Land the View where the user is, the same way a View just created or
  // changed lands: in place when they are looking at its table, otherwise by
  // opening that table with the View applied. The publish reports whether a
  // table was listening, so a list that unmounted between the read and the
  // publish still gets the navigation.
  const appliedToOpenTable =
    tableOnScreen?.target === view.target &&
    deps.applyViewToOpenTable !== undefined &&
    deps.applyViewToOpenTable(view);
  const navigatedTo = appliedToOpenTable ? undefined : viewListPath(view);
  if (navigatedTo !== undefined) {
    deps.goto.navigateTo(navigatedTo);
  }

  return toolSuccess("applied_view", {
    view_id: view.view_id,
    name: view.name,
    target: view.target,
    view_url: viewListPath(view),
    applied_to_open_table: appliedToOpenTable,
    ...(navigatedTo !== undefined && { navigated_to: navigatedTo }),
    ...(verdict.payload.setAsDefault && { set_as_default: true }),
  });
}

export const tool = createClientTool<ApplyViewParams>({
  name: "client_apply_view",
  description:
    "Open a View so the user sees its results: switch the table the user is " +
    "looking at to that View, or, when they are on another page, go to the " +
    "View's table with it applied. Name the View by view_id, or by name to " +
    "have it looked up on the table on screen (or on target). After " +
    "create_view or update_view returns a vw_ id, call this with it so the " +
    "user sees the result. With set_as_default it also makes the View the " +
    "user's default for that table; otherwise it changes nothing on the " +
    "server. Views are made and changed with the create_view, update_view, " +
    "and delete_view tools.",
  input_schema: inputSchema,
  validateInput: isApplyViewParams,
  execute,
  // Like client_navigate: it changes only what is on screen, and picking
  // another View undoes it.
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Open View",
  displayDescription:
    "Switches the table on screen to a View, or opens the View's table.",
  ConfirmationContent: ApplyViewConfirmation,
});
